import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { AddMiniIcon, ArrowChevronRightMiniIcon, QuestionCircleIcon } from '@calendly/ui/components/icons';
import { BareItem } from '@calendly/ui/components/menu';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { LearnMoreButton } from 'components/page_elements';
import { isAvailableFeatureSelector } from 'features/billing';
import { useContextualHelp } from 'features/contextual_help';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { ANALYTICS_CTA_CLICK_PROPS_MAP, ANALYTICS_FLOW_STARTED_PROPS_MAP, CONTENT_PAGES_WITH_DROPDOWN, CONTENT_PAGES_WITHOUT_CTA, EMPTY_STATE_ICON_MAP } from '../constants';
import { getPrimaryButtonOptionsByContentKey } from '../utils';
const containerCss = "c62stuq";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const imageContainerCss = "i1q772yi";
const ImageContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(imageContainerCss, props.className)} />;
});
const textContainerCss = "tjc1iiw";
const TextContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(textContainerCss, props.className)} />;
});
const bodyContainerCss = "bkpvpyf";
const BodyContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bodyContainerCss, props.className)} />;
});
const actionsContainerCss = "a1g1uv5h";
const ActionsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionsContainerCss, props.className)} />;
});
const linkContainerCss = "laytjm9";
const LinkContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(linkContainerCss, props.className)} />;
});
const styledBareItemCss = "snwavl";
const StyledBareItem = forwardRef((props, ref) => <BareItem ref={ref} {...props} className={classNames(styledBareItemCss, props.className)} />);

// i18n-tasks-use t('publisher.empty_state.#{contentKey}.title')
// i18n-tasks-use t('publisher.empty_state.#{contentKey}.subtitle')
// i18n-tasks-use t('publisher.empty_state.#{contentKey}.primary_button')
export const CenteredEmptyState = ({
  contentKey,
  featureKey = null,
  options,
  DropdownComponents = null,
  ButtonOverride = null
}) => {
  const history = useHistory();
  const [toggleETMenu, Dropdown, isOpen, {
    dropdownId
  }] = useDropdown(false, true);
  const isDropdownEmptyState = CONTENT_PAGES_WITH_DROPDOWN.includes(contentKey);
  const iconImage = EMPTY_STATE_ICON_MAP[contentKey];
  const buttonParams = {
    history,
    options
  };
  const primaryButtonOptions = getPrimaryButtonOptionsByContentKey(contentKey, buttonParams);
  const isFeatureAvailable = useSelector(state => isAvailableFeatureSelector(state, {
    featureKey
  }));
  const track = useTrack();
  const {
    pathname
  } = useLocation();
  const {
    open: openContextualHelp
  } = useContextualHelp();
  const onClick = e => {
    const properties = ANALYTICS_CTA_CLICK_PROPS_MAP[contentKey];
    if (primaryButtonOptions?.onClick) primaryButtonOptions.onClick(e);
    if (isDropdownEmptyState) toggleETMenu();
    if (!properties) return;
    track({
      name: TRACKING_EVENT_NAMES.CTA_CLICKED,
      entities: ['attribution', 'organization', 'user'],
      properties
    });
  };
  const ctaButtonProps = {
    ...primaryButtonOptions,
    disabled: featureKey && !isFeatureAvailable,
    onClick,
    'aria-expanded': isDropdownEmptyState ? isOpen : undefined,
    'aria-controls': isDropdownEmptyState ? dropdownId : undefined
  };
  const onClickLearnMore = () => {
    openContextualHelp({
      contentKey
    });
    const properties = ANALYTICS_FLOW_STARTED_PROPS_MAP[contentKey]?.(pathname);
    if (!properties) return;
    track({
      name: TRACKING_EVENT_NAMES.FLOW_STARTED,
      properties
    });
  };
  return <Container data-testid="empty-state-centered">
      {iconImage && <ImageContainer>
          <img src={iconImage} alt="" />
        </ImageContainer>}
      <BodyContainer>
        <TextContainer>
          <HeadingText size="medium">
            {I18n.t(`publisher.empty_state.${contentKey}.title`)}
          </HeadingText>
          <BodyText size="medium" color="muted">
            {I18n.t(`publisher.empty_state.${contentKey}.subtitle`)}
          </BodyText>
        </TextContainer>
      </BodyContainer>
      <ActionsContainer>
        <LearnMoreButton onClick={onClickLearnMore} aria-label={I18n.t('publisher.empty_state.learn_more')}>
          <LinkContainer>
            <QuestionCircleIcon />
            {I18n.t('publisher.empty_state.learn_more')}
            <ArrowChevronRightMiniIcon />
          </LinkContainer>
        </LearnMoreButton>
        <div>
          {!options?.isButtonHidden && (ButtonOverride ? <ButtonOverride /> : !CONTENT_PAGES_WITHOUT_CTA.includes(contentKey) && <Button {...ctaButtonProps}>
                  <AddMiniIcon />
                  {I18n.t(`publisher.empty_state.${contentKey}.primary_button`)}
                </Button>)}
          {isDropdownEmptyState && DropdownComponents && <Dropdown>
              <StyledBareItem>
                <DropdownComponents toggle={toggleETMenu} />
              </StyledBareItem>
            </Dropdown>}
        </div>
      </ActionsContainer>
    </Container>;
};

require("./centered_empty_state.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./centered_empty_state.js");